import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const HowItWorks: React.FC = () => {
  const { isAuthenticated, subscription, user } = useAuth();
  const navigate = useNavigate();

  const handleStepClick = (e: React.MouseEvent, step: number) => {
    if (step === 1) {
      // Step 1 - Browse movies - always allow
      return;
    }

    // Steps 2 & 3 - Create/Watch party
    if (!isAuthenticated) {
      e.preventDefault();
      navigate('/register');
      return;
    }

    if (user?.role === 'admin' || subscription?.has_subscription) {
      navigate('/watch-party/create');
      return;
    }

    e.preventDefault();
    navigate('/subscribe');
  };

  return (
    <div className="relative py-10 lg:py-20 overflow-hidden scroll-reveal">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-netflix-red/5 to-transparent rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-5">
            <span className="w-2 h-2 bg-netflix-red rounded-full animate-pulse"></span>
            <span className="text-white/80 text-sm font-medium">3 Simple Steps</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-3">
            Start Watching <span className="text-transparent bg-clip-text bg-gradient-to-r from-netflix-red to-pink-500">Together</span>
          </h2>
          <p className="text-white/60 text-sm lg:text-lg max-w-xl mx-auto">
            Create unforgettable movie nights with friends, no matter the distance
          </p>
        </div>

        {/* Cards Container */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 max-w-5xl mx-auto">
          {/* Step 1 */}
          <Link to="/movies" className="group relative">
            <div className="absolute inset-0 bg-gradient-to-br from-netflix-red/20 to-red-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative h-full bg-gradient-to-br from-white/[0.08] to-transparent backdrop-blur-sm rounded-2xl p-5 lg:p-7 border border-white/10 group-hover:border-netflix-red/50 transition-all duration-500 group-hover:-translate-y-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-netflix-red to-red-600 flex items-center justify-center text-white text-lg font-black shadow-lg shadow-netflix-red/25">
                  1
                </div>
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-netflix-red/10 transition-all duration-300">
                  <i className="fas fa-film text-xl text-white/80 group-hover:text-netflix-red transition-colors"></i>
                </div>
                <div className="flex-1 h-[1px] bg-gradient-to-r from-netflix-red/30 to-transparent"></div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">Pick a Movie</h3>
              <p className="text-white/50 text-sm lg:text-base leading-relaxed">
                Browse thousands of HD movies and choose what to watch.
              </p>
            </div>
          </Link>

          {/* Step 2 */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => handleStepClick(e, 2)} className="group relative cursor-pointer">
            <div className="absolute inset-0 bg-gradient-to-br from-netflix-red/20 to-red-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative h-full bg-gradient-to-br from-white/[0.08] to-transparent backdrop-blur-sm rounded-2xl p-5 lg:p-7 border border-white/10 group-hover:border-netflix-red/50 transition-all duration-500 group-hover:-translate-y-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-netflix-red to-red-600 flex items-center justify-center text-white text-lg font-black shadow-lg shadow-netflix-red/25">
                  2
                </div>
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-netflix-red/10 transition-all duration-300">
                  <i className="fas fa-share-alt text-xl text-white/80 group-hover:text-netflix-red transition-colors"></i>
                </div>
                <div className="flex-1 h-[1px] bg-gradient-to-r from-netflix-red/30 to-transparent"></div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">Share the Link</h3>
              <p className="text-white/50 text-sm lg:text-base leading-relaxed">
                Create a party and invite friends with one click.
              </p>
            </div>
          </a>

          {/* Step 3 */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => handleStepClick(e, 3)} className="group relative cursor-pointer">
            <div className="absolute inset-0 bg-gradient-to-br from-netflix-red/20 to-red-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="relative h-full bg-gradient-to-br from-white/[0.08] to-transparent backdrop-blur-sm rounded-2xl p-5 lg:p-7 border border-white/10 group-hover:border-netflix-red/50 transition-all duration-500 group-hover:-translate-y-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-netflix-red to-red-600 flex items-center justify-center text-white text-lg font-black shadow-lg shadow-netflix-red/25">
                  3
                </div>
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-netflix-red/10 transition-all duration-300">
                  <i className="fas fa-play text-xl text-white/80 group-hover:text-netflix-red transition-colors"></i>
                </div>
                <div className="flex-1 h-[1px] bg-gradient-to-r from-netflix-red/30 to-transparent"></div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">Watch Together</h3>
              <p className="text-white/50 text-sm lg:text-base leading-relaxed">
                Enjoy synced playback and live chat with friends.
              </p>
            </div>
          </a>
        </div>

        {/* Bottom Features */}
        <div className="flex flex-wrap justify-center gap-4 lg:gap-10 mt-8 lg:mt-12">
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <i className="fas fa-bolt text-yellow-400"></i>
            <span>Instant Sync</span>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <i className="fas fa-comments text-blue-400"></i>
            <span>Live Chat</span>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <i className="fas fa-tv text-purple-400"></i>
            <span>HD Quality</span>
          </div>
          <div className="flex items-center gap-2 text-white/50 text-sm">
            <i className="fas fa-infinity text-green-400"></i>
            <span>Unlimited Parties</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
